/**
 * Audio Lessons Store (Zustand)
 *
 * Owns the Audio Lessons catalog (series list), series episode lists, the open
 * episode + resolved rendition + parsed transcript, and per-episode playback
 * progress. Actions go through the generated `api` client and branch on
 * `result.ok` (mirrors lessonStore / rolePlayStore).
 *
 * This store is SEPARATE from the chat `audioController` (chat TTS) — see
 * docs/specs/mobile/audio-lessons.mobile.md "BOUNDARY". It holds durable catalog
 * + episode state; the live playback position/state comes from the `audioPlayer`
 * (expo-audio) progress hook in the player screen (NOT this store), so
 * high-frequency position updates never churn the store.
 *
 * Progress note: the API has no GET-progress endpoint, so progress saved via
 * `POST /audio/episodes/:slug/progress` is also cached locally (AsyncStorage)
 * for the resume affordance + the series-list resume indicators. This is a
 * local-only UI read-back cache (not a synced sync-DSL domain).
 */

import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from '../services/api';
import type { StoreErrorInfo } from '../generated/api/endpoint-errors';
import { parseTranscript } from '../types/audioTranscript';
import type { AudioTranscript, TeachingLanguage } from '../types/audioTranscript';
import { createLogger } from '../utils/logger';
import type {
  AudioSeriesListResponse,
  AudioSeriesDetailResponse,
  AudioEpisodeDetailResponse,
} from '../generated/api/http-contracts';

const log = createLogger('AudioLessonsStore');

// ── Generated-contract element types (never hand-rolled) ───────────────────────
export type AudioSeriesSummary = AudioSeriesListResponse['series'][number];
export type AudioSeriesHeader = AudioSeriesDetailResponse['series'];
export type AudioEpisodeSummary = AudioSeriesDetailResponse['episodes'][number];
export type AudioEpisodeMeta = AudioEpisodeDetailResponse['episode'];
export type AudioRendition = AudioEpisodeDetailResponse['rendition'];

/** Per-episode progress (mirrors the backend `AudioEpisodeProgress`). */
export interface AudioEpisodeProgress {
  positionMs: number;
  completed: boolean;
  lastPlayedAt: number;
}

/** The two teaching-language sides a rendition can be narrated in. */
export const TEACHING_LANGUAGES: readonly TeachingLanguage[] = ['source', 'target'];

interface AudioLessonsState {
  // ── Catalog (series list) ──────────────────────────────────────────
  series: AudioSeriesSummary[];
  isLoadingSeries: boolean;
  seriesError: StoreErrorInfo | null;
  seriesCourseId: string | null;

  // ── Series detail (episode list) ───────────────────────────────────
  currentSeries: AudioSeriesHeader | null;
  episodes: AudioEpisodeSummary[];
  isLoadingEpisodes: boolean;
  episodesError: StoreErrorInfo | null;
  currentSeriesSlug: string | null;

  // ── Episode detail (player) ────────────────────────────────────────
  currentEpisode: AudioEpisodeMeta | null;
  currentRendition: AudioRendition | null;
  currentTranscript: AudioTranscript | null;
  currentEpisodeSlug: string | null;
  /** Teaching language of the currently LOADED rendition. */
  teachingLanguage: TeachingLanguage | null;
  /** Teaching-language sides proven absent for the current episode (server fell back). */
  missingTeachingLanguages: TeachingLanguage[];
  isLoadingEpisode: boolean;
  episodeError: StoreErrorInfo | null;
  /** The current episode is premium-gated and the user is not entitled (403). */
  isEpisodeForbidden: boolean;

  // ── Progress (per episode) ─────────────────────────────────────────
  progressByEpisode: Record<string, AudioEpisodeProgress>;

  // ── Lesson → linked-episode resolution cache ───────────────────────
  // The API has no lesson→episode lookup, so we reverse-resolve the
  // `lesson_linked` episode by `episode.lessonSlug` and cache the result
  // (`undefined` = not looked up, `null` = none found / fail-closed).
  linkedEpisodeByLesson: Record<string, string | null>;

  // ── Actions ────────────────────────────────────────────────────────
  fetchSeriesList: (courseId: string, level?: string) => Promise<void>;
  fetchSeries: (slug: string) => Promise<void>;
  fetchEpisode: (slug: string, opts?: { teachingLanguage?: TeachingLanguage }) => Promise<boolean>;
  saveProgress: (slug: string, positionMs: number, completed: boolean) => Promise<void>;
  fetchProgress: (courseId?: string) => Promise<void>;
  resolveLinkedEpisode: (lessonSlug: string) => Promise<string | null>;
  hydrateProgress: () => Promise<void>;
  resetPlayer: () => void;
  clearSeriesError: () => void;
  clearEpisodesError: () => void;
  clearEpisodeError: () => void;
  reset: () => void;
}

// ── Local progress cache (AsyncStorage) ────────────────────────────────────────
// Namespaced literal key per the sync naming convention. NOT a synced sync-DSL
// domain — purely a local read-back cache since the API exposes no GET-progress.
const PROGRESS_KEY = '@fluencia/audioLessons/progress';
const PROGRESS_VERSION = 1;

async function persistProgress(items: Record<string, AudioEpisodeProgress>): Promise<void> {
  try {
    await AsyncStorage.setItem(PROGRESS_KEY, JSON.stringify({ v: PROGRESS_VERSION, items }));
  } catch (err) {
    log.debug('Failed to persist audio progress', { err: String(err) });
  }
}

async function readProgress(): Promise<Record<string, AudioEpisodeProgress> | null> {
  try {
    const raw = await AsyncStorage.getItem(PROGRESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: number; items?: unknown };
    if (parsed?.v !== PROGRESS_VERSION || !parsed.items || typeof parsed.items !== 'object') return null;
    return parsed.items as Record<string, AudioEpisodeProgress>;
  } catch {
    return null;
  }
}

const INITIAL_PLAYER_STATE = {
  currentEpisode: null,
  currentRendition: null,
  currentTranscript: null,
  currentEpisodeSlug: null,
  teachingLanguage: null,
  missingTeachingLanguages: [] as TeachingLanguage[],
  isLoadingEpisode: false,
  episodeError: null,
  isEpisodeForbidden: false,
} as const;

export const useAudioLessonsStore = create<AudioLessonsState>((set, get) => ({
  // Initial state
  series: [],
  isLoadingSeries: false,
  seriesError: null,
  seriesCourseId: null,

  currentSeries: null,
  episodes: [],
  isLoadingEpisodes: false,
  episodesError: null,
  currentSeriesSlug: null,

  ...INITIAL_PLAYER_STATE,

  progressByEpisode: {},
  linkedEpisodeByLesson: {},

  /**
   * Fetch the published series for a course (optionally filtered by level).
   * Series replace on success; previously loaded series are preserved on error.
   */
  fetchSeriesList: async (courseId: string, level?: string) => {
    set({ isLoadingSeries: true, seriesError: null });
    const params: Record<string, string> = { courseId };
    if (level) params.level = level;
    const result = await api.listAudioSeries({ params });
    if (result.ok) {
      set({ series: result.data.series, isLoadingSeries: false, seriesCourseId: courseId });
    } else {
      log.warn('Failed to load audio series', { kind: result.kind, courseId });
      set({ isLoadingSeries: false, seriesError: result });
    }
  },

  /**
   * Fetch one series header + its episode summaries (no transcript/audio here).
   */
  fetchSeries: async (slug: string) => {
    set({ isLoadingEpisodes: true, episodesError: null, currentSeriesSlug: slug });
    const result = await api.getAudioSeries(slug);
    if (result.ok) {
      set({
        currentSeries: result.data.series,
        episodes: result.data.episodes,
        isLoadingEpisodes: false,
      });
    } else {
      log.warn('Failed to load audio series detail', { kind: result.kind, slug });
      set({ isLoadingEpisodes: false, episodesError: result });
    }
  },

  /**
   * Fetch an episode + the ONE server-resolved rendition (incl. transcript).
   * `opts.teachingLanguage` overrides the user's preference for this fetch.
   *
   * Returns true on success. On a teaching-language SWAP failure the previous
   * rendition is left intact (position preserved — AUDIO-LESSONS-005); a brand-new
   * episode load surfaces the error so the player can show retry / the premium gate.
   */
  fetchEpisode: async (slug: string, opts) => {
    const requested = opts?.teachingLanguage;
    const isNewEpisode = get().currentEpisodeSlug !== slug;
    // Stamp the slug up front so the player binds error/loading to THIS episode
    // even when the load fails (currentEpisodeSlug would otherwise stay unset).
    // A brand-new episode also clears the prior rendition/transcript; a same-slug
    // teaching-language SWAP keeps it so a failed swap preserves the position.
    set({
      isLoadingEpisode: true,
      episodeError: null,
      currentEpisodeSlug: slug,
      ...(isNewEpisode
        ? {
            isEpisodeForbidden: false,
            currentEpisode: null,
            currentRendition: null,
            currentTranscript: null,
            teachingLanguage: null,
            missingTeachingLanguages: [],
          }
        : {}),
    });

    const result = await api.getAudioEpisode(
      slug,
      requested ? { params: { teachingLanguage: requested } } : undefined,
    );

    if (result.ok) {
      const { episode, rendition } = result.data;
      const returnedLang = rendition.teachingLanguage as TeachingLanguage;
      // A specific teachingLanguage was requested but the server resolved the
      // OTHER side → the requested side is absent for this episode. Remember it
      // so the toggle hides that option (AUDIO-LESSONS-005).
      let missing = isNewEpisode ? [] : get().missingTeachingLanguages;
      if (requested && returnedLang !== requested && !missing.includes(requested)) {
        missing = [...missing, requested];
      }
      set({
        currentEpisode: episode,
        currentRendition: rendition,
        currentTranscript: parseTranscript(rendition.transcript),
        currentEpisodeSlug: slug,
        teachingLanguage: returnedLang,
        missingTeachingLanguages: missing,
        isLoadingEpisode: false,
        isEpisodeForbidden: false,
      });
      return true;
    }

    const forbidden = result.kind === 'domain' && result.error.code === 'FORBIDDEN';
    log.warn('Failed to load audio episode', { kind: result.kind, slug, forbidden });
    set({
      isLoadingEpisode: false,
      episodeError: result,
      ...(forbidden ? { isEpisodeForbidden: true } : {}),
    });
    return false;
  },

  /**
   * Persist playback progress for an episode. Optimistically updates the local
   * cache (resume affordance survives app kill) then POSTs to the backend. A
   * failed POST is non-blocking — the local position is kept and re-flushed
   * later (AUDIO-LESSONS-008).
   */
  saveProgress: async (slug: string, positionMs: number, completed: boolean) => {
    const safePosition = Math.max(0, Math.round(positionMs));
    const entry: AudioEpisodeProgress = { positionMs: safePosition, completed, lastPlayedAt: Date.now() };
    const next = { ...get().progressByEpisode, [slug]: entry };
    set({ progressByEpisode: next });
    void persistProgress(next);

    const result = await api.saveAudioProgress(slug, { positionMs: safePosition, completed });
    if (!result.ok) {
      log.warn('Failed to save audio progress (kept locally)', { kind: result.kind, slug });
    }
  },

  /**
   * Fetch the caller's server-side progress (the authoritative read path) and
   * merge into the local map, newest-`lastPlayedAt`-wins so an unflushed local
   * save isn't clobbered by a staler server row. Persists the merge so the
   * offline cache stays current. Non-blocking on failure (local cache stands in).
   */
  fetchProgress: async (courseId?: string) => {
    const result = await api.listAudioProgress(courseId ? { params: { courseId } } : undefined);
    if (!result.ok) {
      log.warn('Failed to load audio progress (using local cache)', { kind: result.kind });
      return;
    }
    const merged = { ...get().progressByEpisode };
    for (const p of result.data.progress) {
      const entry: AudioEpisodeProgress = {
        positionMs: p.positionMs,
        completed: p.completed,
        lastPlayedAt: Date.parse(p.lastPlayedAt) || 0,
      };
      const existing = merged[p.episodeSlug];
      if (!existing || entry.lastPlayedAt >= existing.lastPlayedAt) merged[p.episodeSlug] = entry;
    }
    set({ progressByEpisode: merged });
    void persistProgress(merged);
  },

  /**
   * Resolve the lesson's linked episode slug via the dedicated endpoint
   * (`GET /audio/episodes/by-lesson/:lessonSlug`). Cached per lesson
   * (`undefined`=unknown, `null`=none). Fails closed to `null` WITHOUT caching on
   * a transient error, so the "Listen on the go" button reappears once
   * connectivity recovers (AUDIO-LESSONS-009).
   */
  resolveLinkedEpisode: async (lessonSlug: string) => {
    const cached = get().linkedEpisodeByLesson[lessonSlug];
    if (cached !== undefined) return cached;

    const result = await api.getAudioEpisodeByLesson(lessonSlug);
    if (!result.ok) return null; // transient — do NOT cache, retry next mount

    const slug = result.data.episodeSlug;
    set({ linkedEpisodeByLesson: { ...get().linkedEpisodeByLesson, [lessonSlug]: slug } });
    return slug;
  },

  /** Load persisted progress from AsyncStorage; in-session entries win on merge. */
  hydrateProgress: async () => {
    const items = await readProgress();
    if (items) set({ progressByEpisode: { ...items, ...get().progressByEpisode } });
  },

  /** Clear the open-episode/player slice (call when opening a NEW episode). */
  resetPlayer: () => set({ ...INITIAL_PLAYER_STATE }),

  clearSeriesError: () => set({ seriesError: null }),
  clearEpisodesError: () => set({ episodesError: null }),
  clearEpisodeError: () => set({ episodeError: null }),

  /** Full reset to initial (E2E affordance — mirrors rolePlayStore.reset). */
  reset: () =>
    set({
      series: [],
      isLoadingSeries: false,
      seriesError: null,
      seriesCourseId: null,
      currentSeries: null,
      episodes: [],
      isLoadingEpisodes: false,
      episodesError: null,
      currentSeriesSlug: null,
      ...INITIAL_PLAYER_STATE,
      progressByEpisode: {},
      linkedEpisodeByLesson: {},
    }),
}));

// ── Non-React imperative accessors ─────────────────────────────────────────────

/** Read saved progress for an episode outside React (e.g. resume seek). */
export function getEpisodeProgress(slug: string): AudioEpisodeProgress | null {
  return useAudioLessonsStore.getState().progressByEpisode[slug] ?? null;
}
